import React from 'react';
import { Skeleton } from '@mui/material';
import { MuiCard } from '@ace/ace-ui-web';
import styles from './SkeletonPerformancePGCards.module.css';

const CARD_COUNT = 5;

/**
 * Single skeleton card mimicking the PerformanceCard layout:
 *   - Title row
 *   - Rank (left) and YoY change % (right) with arrow icon
 *   - Two bar columns: current year (blue, taller) and benchmark (gray, shorter)
 */
const SkeletonPerformancePGCard: React.FC = () => {
  return (
    <MuiCard
      borderRadius={2}
      elevation={1}
      height={264}
      padding={2}
      variant="elevation"
      width={239}>
      <div className={styles.cardContent}>
        {/* Title */}
        <Skeleton
          variant="text"
          width="72%"
          height={24}
          sx={{ backgroundColor: 'rgba(0,0,0,0.08)' }}
        />

        {/* Rank & Change Percent Row */}
        <div className={styles.rankPercentRow}>
          {/* Rank Section: large rank value + small "Rank" label below */}
          <div className={styles.rankSection}>
            <Skeleton
              variant="text"
              width={88}
              height={32}
              sx={{ backgroundColor: 'rgba(0,0,0,0.08)' }}
            />
            <Skeleton
              variant="text"
              width={38}
              height={18}
              sx={{ backgroundColor: 'rgba(0,0,0,0.06)' }}
            />
          </div>

          {/* Change Percent Section: arrow icon + value, then "YoY" label right-aligned */}
          <div className={styles.changePercentSection}>
            <div className={styles.changePercentWithIcon}>
              <Skeleton
                variant="circular"
                width={18}
                height={18}
                sx={{ backgroundColor: 'rgba(0,0,0,0.08)' }}
              />
              <Skeleton
                variant="text"
                width={56}
                height={32}
                sx={{ backgroundColor: 'rgba(0,0,0,0.08)' }}
              />
            </div>
            <Skeleton
              variant="text"
              width={28}
              height={18}
              sx={{ backgroundColor: 'rgba(0,0,0,0.06)', alignSelf: 'flex-end' }}
            />
          </div>
        </div>

        {/* Bars Container: two bar groups aligned to the bottom */}
        <div className={styles.barsContainer}>
          {/* Current Year bar — taller, tinted blue */}
          <div className={styles.barGroup}>
            <Skeleton
              variant="text"
              width={58}
              height={20}
              sx={{ backgroundColor: 'rgba(0,0,0,0.08)' }}
            />
            <Skeleton
              variant="rectangular"
              width={72}
              height={62}
              sx={{ borderRadius: '4px 4px 0 0', backgroundColor: 'rgba(30,136,229,0.18)' }}
            />
          </div>

          {/* Benchmark bar — shorter, tinted gray */}
          <div className={styles.barGroup}>
            <Skeleton
              variant="text"
              width={58}
              height={20}
              sx={{ backgroundColor: 'rgba(0,0,0,0.08)' }}
            />
            <Skeleton
              variant="rectangular"
              width={72}
              height={46}
              sx={{ borderRadius: '4px 4px 0 0', backgroundColor: 'rgba(117,117,117,0.18)' }}
            />
          </div>
        </div>
      </div>
    </MuiCard>
  );
};

/**
 * SkeletonPerformancePGCards — Loading skeleton for the Performance Peer Group Overview section.
 *
 * Renders the full section structure (title, scrollable card row, legend) using MUI Skeleton
 * elements that match the dimensions and layout of the real PerformancePeerGroup content.
 * Each of the five cards mirrors the PerformanceCard layout exactly.
 */
const SkeletonPerformancePGCards: React.FC = () => {
  return (
    <div className={styles.performancePeerGroupContainer}>
      {/* Section title placeholder */}
      <Skeleton
        variant="text"
        width={300}
        height={52}
        sx={{ margin: '0 auto', backgroundColor: 'rgba(0,0,0,0.08)' }}
      />

      {/* Horizontally scrollable card row */}
      <div className={styles.performancePeerGroupCardWrapper}>
        <div className={styles.performancePeerGroupCardContainer}>
          {Array.from({ length: CARD_COUNT }).map((_, index) => (
            <SkeletonPerformancePGCard key={index} />
          ))}
        </div>
      </div>

      {/* Legend: two items with colored dot + label */}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <Skeleton
            variant="circular"
            width={10}
            height={10}
            sx={{ backgroundColor: 'rgba(30,136,229,0.35)' }}
          />
          <Skeleton
            variant="text"
            width={78}
            height={20}
            sx={{ backgroundColor: 'rgba(0,0,0,0.07)' }}
          />
        </div>
        <div className={styles.legendItem}>
          <Skeleton
            variant="circular"
            width={10}
            height={10}
            sx={{ backgroundColor: 'rgba(117,117,117,0.35)' }}
          />
          <Skeleton
            variant="text"
            width={98}
            height={20}
            sx={{ backgroundColor: 'rgba(0,0,0,0.07)' }}
          />
        </div>
      </div>
    </div>
  );
};

export default SkeletonPerformancePGCards;
